import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Paper, Typography, Button, Chip, CircularProgress, Snackbar, Alert } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PhoneIcon from "@mui/icons-material/Phone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PeopleIcon from "@mui/icons-material/People";
import CampaignIcon from "@mui/icons-material/Campaign";
import GroupIcon from "@mui/icons-material/Group";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { getWhatsAppClientDashboard, getClientChats, migrateClientChats, diagnoseWhatsAppClient, syncWhatsAppDisplayNumber, switchWhatsAppClientNumber, resubscribeWhatsAppWebhook, testWhatsAppWebhook } from "../api/adminApi";
import MonitoringDashboard from "../components/monitoring/MonitoringDashboard";

const statusLabels = {
  not_started: "Not Started", fb_connected: "FB Connected", pending: "Pending",
  payment_pending: "Payment Needed", onboarding: "Setting Up", details_submitted: "Details Submitted",
  otp_ready: "OTP Ready", otp_verified: "OTP Verified", active: "Active", suspended: "Suspended"
};
const statusColors = {
  not_started: "#64748b", fb_connected: "#f59e0b", pending: "#f59e0b",
  payment_pending: "#f59e0b", onboarding: "#3b82f6", details_submitted: "#8b5cf6",
  otp_ready: "#3b82f6", otp_verified: "#22c55e", active: "#22c55e", suspended: "#ef4444"
};

const DashboardCard = ({ title, children, sx }) => (
  <Paper className="glass-card" sx={{ p: 3.5, ...sx }}>
    <Typography fontWeight={700} fontSize={15} color="#1e293b" mb={2.5}>{title}</Typography>
    {children}
  </Paper>
);

const LinkItem = ({ href, label }) => (
  <Box component="a" href={href} target="_blank" rel="noopener noreferrer"
    sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: 2, textDecoration: "none", color: "#475569", bgcolor: "rgba(99,102,241,0.06)", "&:hover": { bgcolor: "rgba(99,102,241,0.12)" } }}>
    <OpenInNewIcon sx={{ fontSize: 16, color: "#6366f1", flexShrink: 0 }} />
    <Typography variant="body2" fontWeight={500}>{label}</Typography>
  </Box>
);

const WhatsAppClientDashboard = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chats, setChats] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [switchNum, setSwitchNum] = useState("");
  const [switching, setSwitching] = useState(false);
  const [resubscribing, setResubscribing] = useState(false);
  const [selfTesting, setSelfTesting] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const loadChats = () => {
    getClientChats(clientId).then(setChats).catch(() => setChats(null));
  };

  useEffect(() => {
    setLoading(true);
    getWhatsAppClientDashboard(clientId)
      .then(setData)
      .catch(err => setSnack({ open: true, message: err.message, severity: "error" }))
      .finally(() => setLoading(false));
    loadChats();
  }, [clientId]);

  const handleMigrate = () => {
    setMigrating(true);
    migrateClientChats(clientId)
      .then((res) => {
        setSnack({ open: true, message: res.message, severity: "success" });
        loadChats();
      })
      .catch((err) => setSnack({ open: true, message: err.message, severity: "error" }))
      .finally(() => setMigrating(false));
  };

  const handleDiagnose = () => {
    setDiagnosing(true);
    setDiagnosis(null);
    diagnoseWhatsAppClient(clientId)
      .then(setDiagnosis)
      .catch((err) => setSnack({ open: true, message: err.message, severity: "error" }))
      .finally(() => setDiagnosing(false));
  };

  const handleSyncNumber = () => {
    setSyncing(true);
    syncWhatsAppDisplayNumber(clientId)
      .then((res) => {
        setSnack({ open: true, message: res.message, severity: "success" });
        getWhatsAppClientDashboard(clientId).then(setData).catch(() => {});
      })
      .catch((err) => setSnack({ open: true, message: err.message, severity: "error" }))
      .finally(() => setSyncing(false));
  };

  const handleSwitchNumber = () => {
    const num = switchNum.trim();
    if (!num) { setSnack({ open: true, message: "Paste a phoneNumberId first (from Webhook Hits list)", severity: "warning" }); return; }
    setSwitching(true);
    switchWhatsAppClientNumber(clientId, { phoneNumberId: num, reason: "Switched to number receiving messages" })
      .then((res) => {
        setSnack({ open: true, message: res.message, severity: "success" });
        setSwitchNum("");
        getWhatsAppClientDashboard(clientId).then(setData).catch(() => {});
        loadChats();
      })
      .catch((err) => setSnack({ open: true, message: err.message, severity: "error" }))
      .finally(() => setSwitching(false));
  };

  const handleResubscribe = () => {
    if (!window.confirm("Re-subscribe webhook on this WABA with the account's saved token? After this, Diagnose shows exactly which app is subscribed.")) return;
    setResubscribing(true);
    resubscribeWhatsAppWebhook(clientId)
      .then((res) => setSnack({ open: true, message: res.message, severity: res.ok ? "success" : "warning" }))
      .catch((err) => setSnack({ open: true, message: err.message, severity: "error" }))
      .finally(() => setResubscribing(false));
  };

  const handleSelfTest = () => {
    setSelfTesting(true);
    testWhatsAppWebhook(clientId)
      .then((res) => setSnack({ open: true, message: res.message, severity: "success" }))
      .catch((err) => setSnack({ open: true, message: err.message, severity: "error" }))
      .finally(() => setSelfTesting(false));
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  if (!data) return <Box sx={{ p: 4 }}><Typography color="#ef4444">Dashboard data not available</Typography></Box>;

  const { user, waAccount, conversationCount } = data;
  const isActive = waAccount.onboardingStatus === "active";
  const needsPayment = waAccount.onboardingStatus === "payment_pending" || !waAccount.paymentStatus;
  const hasPhoneNumber = !!waAccount.phoneNumberId;
  const hasWaba = !!waAccount.wabaId || !!waAccount.clientWabaId;
  const isApiSetup = hasPhoneNumber && hasWaba && waAccount.webhookSubscribed;
  const accountStatus = isActive ? "active" : waAccount.onboardingStatus === "suspended" ? "suspended" : "in_setup";

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <Button onClick={() => navigate("/whatsapp")} startIcon={<ArrowBackIcon />} sx={{ color: "#64748b", textTransform: "none", fontWeight: 600 }}>
          Back to WhatsApp
        </Button>
        <Typography fontWeight={800} fontSize={24} color="#1e293b">Client Dashboard</Typography>
      </Box>

      <Box sx={{ display: "grid", gap: "27px", mb: 5 }}>
        <Paper className="glass-card" sx={{ p: 3.5, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography fontWeight={700} fontSize={18} color="#1e293b">{user.fullName}</Typography>
            <Typography variant="body2" color="#64748b">{user.email}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Chip label={statusLabels[waAccount.onboardingStatus] || waAccount.onboardingStatus} size="small" sx={{ color: "#fff", bgcolor: statusColors[waAccount.onboardingStatus] || "#64748b", fontWeight: 600, fontSize: 12 }} />
            <Button size="small" variant="outlined" onClick={() => navigate(`/users/${user._id}`)} sx={{ textTransform: "none", borderRadius: 1.5, fontSize: 12 }}>
              Full Profile
            </Button>
          </Box>
        </Paper>

        {needsPayment && (
          <Paper sx={{ p: 3.5, borderRadius: 3, border: "1px solid #f59e0b", bgcolor: "rgba(245,158,11,0.08)", display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <WarningAmberIcon sx={{ color: "#f59e0b", fontSize: 28 }} />
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={700} fontSize={14} color="#92400e">Payment Method Required</Typography>
              <Typography variant="body2" color="#a16207">{user.fullName} needs to add a payment method in Meta to send business-initiated messages.</Typography>
            </Box>
            <Button variant="contained" size="small" href="https://business.facebook.com/wa/manage/payment" target="_blank" rel="noopener noreferrer" startIcon={<OpenInNewIcon />} sx={{ bgcolor: "#f59e0b", "&:hover": { bgcolor: "#d97706" }, textTransform: "none", borderRadius: 1.5, fontSize: 12, whiteSpace: "nowrap" }}>
              Add Payment
            </Button>
          </Paper>
        )}

        {!isActive && !needsPayment && (
          <Paper sx={{ p: 3.5, borderRadius: 3, border: "1px solid #3b82f6", bgcolor: "rgba(59,130,246,0.08)", display: "flex", alignItems: "center", gap: 2 }}>
            <CheckCircleIcon sx={{ color: "#3b82f6", fontSize: 28 }} />
            <Box>
              <Typography fontWeight={700} fontSize={14} color="#1e40af">Setup In Progress</Typography>
              <Typography variant="body2" color="#2563eb">Onboarding status: {statusLabels[waAccount.onboardingStatus]}</Typography>
            </Box>
          </Paper>
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: "27px", mb: 5 }}>
        <Paper className="glass-card" sx={{ p: 3.5, display: "flex", alignItems: "center", gap: 3 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "14px", bgcolor: "rgba(99,102,241,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <PeopleIcon sx={{ color: "#6366f1", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="caption" color="#64748b" fontWeight={600}>Contacts</Typography>
            <Typography fontWeight={800} fontSize={22} color="#1e293b">{conversationCount}</Typography>
          </Box>
        </Paper>
        <Paper className="glass-card" sx={{ p: 3.5, display: "flex", alignItems: "center", gap: 3 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "14px", bgcolor: "rgba(16,185,129,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <GroupIcon sx={{ color: "#10b981", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="caption" color="#64748b" fontWeight={600}>Groups</Typography>
            <Typography fontWeight={800} fontSize={22} color="#1e293b">0</Typography>
          </Box>
        </Paper>
        <Paper className="glass-card" sx={{ p: 3.5, display: "flex", alignItems: "center", gap: 3 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "14px", bgcolor: "rgba(245,158,11,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <CampaignIcon sx={{ color: "#f59e0b", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="caption" color="#64748b" fontWeight={600}>Campaigns</Typography>
            <Typography fontWeight={800} fontSize={22} color="#1e293b">0</Typography>
          </Box>
        </Paper>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: "27px", mb: 5 }}>
        <DashboardCard title="Account Details">
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <DetailRow label="WhatsApp Number" value={waAccount.whatsappBusinessNumber || waAccount.businessPhone || "Not set"} icon={<PhoneIcon sx={{ fontSize: 16, color: "#6366f1" }} />} />
            <DetailRow label="Business Name" value={waAccount.businessName || "Not set"} />
            <DetailRow label="WABA ID" value={waAccount.clientWabaId || waAccount.wabaId || "Not set"} mono />
            <DetailRow label="Phone Number ID" value={waAccount.phoneNumberId || "Not set"} mono />
            <DetailRow label="Business Manager ID" value={waAccount.facebookBusinessManagerId || "Not set"} mono />
            <DetailRow label="Business Manager Name" value={waAccount.facebookBusinessManagerName || "Not set"} />
            {waAccount.twoFactorPin && <DetailRow label="2FA PIN" value={waAccount.twoFactorPin} />}
            <DetailRow label="Payment Status" value={waAccount.paymentStatus || "Not set"} />
            <DetailRow label="Plan" value={user.plan?.name || "No plan"} />
            <DetailRow label="Joined" value={user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"} />
          </Box>
        </DashboardCard>

        <DashboardCard title="API Setup">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SetupStatus label="Account Status" status={accountStatus} />
            <SetupStatus label="Phone Number Connected" status={hasPhoneNumber ? "active" : "inactive"} />
            <SetupStatus label="WABA Connected" status={hasWaba ? "active" : "inactive"} />
            <SetupStatus label="Webhook Subscribed" status={waAccount.webhookSubscribed ? "active" : "inactive"} />
            <SetupStatus label="API Setup Complete" status={isApiSetup ? "active" : "inactive"} />
          </Box>
        </DashboardCard>
      </Box>

      {/* Monitoring Dashboard */}
      <MonitoringDashboard clientId={clientId} />

      <Box sx={{ display: "grid", gap: "27px", mb: 5 }}>
        <DashboardCard title="Diagnose (Why no messages?)">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
              <Typography variant="body2" color="#64748b">
                Current display: <span style={{ fontFamily: "'Courier New', monospace", color: "#1e293b", fontWeight: 700 }}>{waAccount.whatsappBusinessNumber || "NOT SET"}</span>
              </Typography>
              <Button variant="contained" size="small" onClick={handleSyncNumber} disabled={syncing} startIcon={syncing ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
                sx={{ bgcolor: "#0891b2", "&:hover": { bgcolor: "#0e7490" }, textTransform: "none", borderRadius: 1.5, fontSize: 12, whiteSpace: "nowrap" }}>
                {syncing ? "Syncing…" : "Update Display Number from Meta"}
              </Button>
            </Box>
            <Typography variant="body2" color="#64748b">
              Runs all Meta checks using the credentials saved on this account — token, phone status, WABA, webhook subscription.
            </Typography>
          </Box>
            <Button variant="contained" size="small" onClick={handleDiagnose} disabled={diagnosing} startIcon={diagnosing ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
              sx={{ bgcolor: "#7c3aed", "&:hover": { bgcolor: "#6d28d9" }, textTransform: "none", borderRadius: 1.5, fontSize: 12, whiteSpace: "nowrap" }}>
              {diagnosing ? "Checking…" : "Run Diagnose"}
            </Button>
            <Button variant="contained" size="small" onClick={handleResubscribe} disabled={resubscribing} startIcon={resubscribing ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
              sx={{ bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" }, textTransform: "none", borderRadius: 1.5, fontSize: 12, whiteSpace: "nowrap" }}>
              {resubscribing ? "Re-subscribing…" : "Re-Subscribe Webhook"}
            </Button>
            <Button variant="outlined" size="small" onClick={handleSelfTest} disabled={selfTesting} startIcon={selfTesting ? <CircularProgress size={14} sx={{ color: "#2563eb" }} /> : null}
              sx={{ color: "#2563eb", borderColor: "#93c5fd", textTransform: "none", borderRadius: 1.5, fontSize: 12, whiteSpace: "nowrap" }}>
              {selfTesting ? "Testing…" : "Self-Test Webhook"}
            </Button>
          <Paper sx={{ p: 1.5, borderRadius: 2, border: "1px dashed #a5b4fc", bgcolor: "rgba(199,210,254,0.15)", mt: 2, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography variant="body2" color="#3730a3" fontWeight={600}>
              Messages receiving on wrong number? Paste that phoneNumberId here to switch this client to it:
            </Typography>
            <Box component="input" value={switchNum} onChange={(e) => setSwitchNum(e.target.value)} placeholder="e.g. 1226626130538316"
              sx={{ flex: 1, minWidth: 200, p: "8px 12px", borderRadius: 1.5, border: "1px solid #c7d2fe", bgcolor: "#ffffff", fontSize: 13, fontFamily: "'Courier New', monospace" }} />
            <Button variant="contained" size="small" onClick={handleSwitchNumber} disabled={switching} startIcon={switching ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
              sx={{ bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" }, textTransform: "none", borderRadius: 1.5, fontSize: 12, whiteSpace: "nowrap" }}>
              {switching ? "Switching…" : "Switch Client to This Number"}
            </Button>
          </Paper>
          {diagnosis && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {diagnosis.checks.map((c, i) => (
                <Paper key={i} sx={{ p: 1.5, borderRadius: 2, border: c.ok ? "1px solid #bbf7d0" : "1px solid #fecaca", bgcolor: c.ok ? "rgba(220,252,231,0.35)" : "rgba(254,226,226,0.35)", display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  {c.ok ? <CheckCircleIcon sx={{ fontSize: 18, color: "#22c55e", flexShrink: 0, mt: 0.2 }} /> : <CancelIcon sx={{ fontSize: 18, color: "#ef4444", flexShrink: 0, mt: 0.2 }} />}
                  <Box>
                    <Typography variant="body2" color="#1e293b" fontWeight={700}>{c.step}</Typography>
                    <Typography variant="body2" color="#475569" sx={{ wordBreak: "break-word" }}>{c.detail}</Typography>
                  </Box>
                </Paper>
              ))}
              <Typography variant="body2" fontWeight={600} color={diagnosis.ok ? "#16a34a" : "#dc2626"}>{diagnosis.summary}</Typography>
            </Box>
          )}
        </DashboardCard>
      </Box>

      <Box sx={{ display: "grid", gap: "27px", mb: 5 }}>
        <DashboardCard title="Chat History">
          {!chats ? (
            <Typography color="#64748b" variant="body2">Unable to load chat history.</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="#475569" fontWeight={600}>
                    Current Number: <span style={{ fontFamily: "'Courier New', monospace", color: "#1e293b" }}>{chats.phoneNumberId || "NOT SET"}</span>
                  </Typography>
                  <Typography variant="body2" color="#475569" fontWeight={600}>
                    Conversations on current number:{" "}
                    <span style={{ color: chats.currentCount > 0 ? "#22c55e" : "#ef4444", fontWeight: 800 }}>{chats.currentCount}</span>
                  </Typography>
                </Box>
                <Button variant="contained" size="small" onClick={handleMigrate} disabled={migrating} startIcon={migrating ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
                  sx={{ bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" }, textTransform: "none", borderRadius: 1.5, fontSize: 12, whiteSpace: "nowrap" }}>
                  {migrating ? "Migrating…" : "Migrate Old Chats → Current"}
                </Button>
              </Box>

              {chats.history.filter((h) => h.count > 0).length === 0 ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 18, color: "#22c55e" }} />
                  <Typography color="#64748b" variant="body2">No orphaned conversations found — all chats are under the current number.</Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {chats.history.filter((h) => h.count > 0).map((h, i) => (
                    <Paper key={i} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #fde68a", bgcolor: "rgba(253,230,138,0.2)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                      <Typography variant="body2" color="#92400e" fontWeight={600} sx={{ fontFamily: "'Courier New', monospace", fontSize: 12 }}>
                        {h.chatbotId} — {h.count} conversation(s) still on old number
                      </Typography>
                      <Typography variant="caption" color="#a16207">
                        {h.reason ? `Reason: ${h.reason} · ` : ""}{h.replacedAt ? new Date(h.replacedAt).toLocaleString() : ""}
                      </Typography>
                    </Paper>
                  ))}
                  <Typography variant="caption" color="#64748b">Click "Migrate Old Chats → Current" to pull these into the current number.</Typography>
                </Box>
              )}
            </Box>
          )}
        </DashboardCard>
      </Box>

      <Box sx={{ display: "grid", gap: "27px", mb: 5 }}>
        <DashboardCard title="WABA History">
          {(waAccount.wabaHistory && waAccount.wabaHistory.length > 0) ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {waAccount.wabaHistory.map((h, i) => (
                <Paper key={i} sx={{ p: 2, borderRadius: 2, border: "1px solid #fecaca", bgcolor: "rgba(254,226,226,0.35)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                    <Typography variant="caption" color="#64748b" fontWeight={600}>Replaced WABA</Typography>
                    <Typography variant="body2" color="#1e293b" fontWeight={600} sx={{ fontFamily: "'Courier New', monospace", fontSize: 12 }}>
                      {h.wabaName ? `"${h.wabaName}" · ` : ""}WABA {h.wabaId || "—"} · Phone {h.phoneNumberId || "—"}
                    </Typography>
                    {(h.facebookBusinessManagerId || h.facebookBusinessManagerName) && (
                      <Typography variant="caption" color="#3730a3" fontWeight={600}>
                        BM: {h.facebookBusinessManagerName || "—"}{h.facebookBusinessManagerId ? ` (ID: ${h.facebookBusinessManagerId})` : ""}
                      </Typography>
                    )}
                    {h.reason && <Typography variant="caption" color="#b45309" fontWeight={600}>Reason: {h.reason}</Typography>}
                  </Box>
                  <Typography variant="caption" color="#64748b">{h.replacedAt ? new Date(h.replacedAt).toLocaleString() : "—"}</Typography>
                </Paper>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 18, color: "#22c55e" }} />
              <Typography color="#64748b" variant="body2">No previous WABAs — current WABA has never been replaced.</Typography>
            </Box>
          )}
        </DashboardCard>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "27px", mb: 5 }}>
        <DashboardCard title="Campaigns">
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CampaignIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
            <Typography color="#64748b" variant="body2" mb={2}>Create your first campaign to reach customers</Typography>
            <Button variant="contained" href="https://business.facebook.com/wa/manage/campaigns" target="_blank" rel="noopener noreferrer" startIcon={<OpenInNewIcon />} sx={{ bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" }, textTransform: "none", borderRadius: 2 }}>
              Create Campaign
            </Button>
          </Box>
        </DashboardCard>

        <DashboardCard title="Important Links">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <LinkItem href="https://business.facebook.com/wa/manage/payment" label="WhatsApp Messages Payment (direct to Facebook)" />
            <LinkItem href="https://business.facebook.com/wa/manage/accounts" label="WhatsApp Business Manager" />
            <LinkItem href={`https://business.facebook.com/wa/manage/message-templates?waba_id=${waAccount.wabaId || waAccount.clientWabaId || ""}`} label="Create WhatsApp Template" />
            <LinkItem href="https://business.facebook.com/wa/manage/phone-numbers" label="Green Tick Tutorial" />
            <LinkItem href="https://business.facebook.com/wa/manage/billing" label="WhatsApp Billing & Invoice" />
            <LinkItem href="https://business.facebook.com/wa/help" label="WhatsApp Direct Support" />
            <LinkItem href={`https://business.facebook.com/wa/manage/accounts?waba_id=${waAccount.wabaId || waAccount.clientWabaId || ""}`} label="WhatsApp Linked Accounts" />
          </Box>
        </DashboardCard>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: "100%", borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

const DetailRow = ({ label, value, icon, mono }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
    <Typography variant="caption" color="#64748b" fontWeight={600}>{label}</Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {icon}
      <Typography variant="body2" color="#1e293b" fontWeight={600} sx={mono ? { fontFamily: "'Courier New', monospace", fontSize: 12 } : {}}>{value}</Typography>
    </Box>
  </Box>
);

const SetupStatus = ({ label, status }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5 }}>
    <Typography variant="body2" color="#475569">{label}</Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {status === "active" ? <CheckCircleIcon sx={{ fontSize: 16, color: "#22c55e" }} /> : status === "in_setup" ? <WarningAmberIcon sx={{ fontSize: 16, color: "#f59e0b" }} /> : <CancelIcon sx={{ fontSize: 16, color: "#ef4444" }} />}
      <Typography variant="caption" fontWeight={600} color={status === "active" ? "#22c55e" : status === "in_setup" ? "#f59e0b" : "#ef4444"}>
        {status === "active" ? "Completed" : status === "in_setup" ? "In Progress" : "Not Set"}
      </Typography>
    </Box>
  </Box>
);

export default WhatsAppClientDashboard;
